// use-web-push-channel — высокоуровневый контроллер канала «Push в браузере».
//
// Связывает три слоя, которые до этого существовали, но не были соединены:
//   1. usePushPermission       — статус + запрос Notification.permission
//   2. useWebPush              — PushManager.subscribe/unsubscribe (VAPID)
//   3. pushTokens.registerToken / unregister (tRPC) — регистрация endpoint в БД
//      + Novu credentials (см. modules/push-tokens/mutations/register-token.ts)
//
// Потребители: settings/notifications (тумблер webPush) и push-init (авто-
// восстановление подписки после login + pre-prompt после онбординга).
//
// enable()/disable() стабильны по идентичности (через refs) — их можно класть
// в deps useEffect без зацикливания.
import { useCallback, useMemo, useRef } from "react";
import { usePushPermission } from "@/hooks/use-push-permission";
import { type UseWebPushOptions, useWebPush } from "@/hooks/use-web-push";
import { api } from "@/lib/trpc-client";
import { getVapidPublicKey } from "@/lib/web-push-config";

export type EnableReason = "unsupported" | "noVapid" | "denied" | "error";
export type EnableResult = { ok: true } | { ok: false; reason: EnableReason };

export interface WebPushChannel {
	/** Браузер поддерживает Web Push (Service Worker + PushManager, не Capacitor). */
	supported: boolean;
	/** VAPID public key сконфигурирован (VITE_VAPID_PUBLIC_KEY). */
	vapidConfigured: boolean;
	/** 'granted' | 'denied' | 'default' | 'unsupported'. */
	permission: ReturnType<typeof usePushPermission>["permission"];
	/** Активная PushSubscription существует. */
	isSubscribed: boolean;
	/** Идёт subscribe/unsubscribe/register. */
	isBusy: boolean;
	/** Запросить разрешение (если нужно) + подписаться + зарегистрировать токен. */
	enable: () => Promise<EnableResult>;
	/** Отписаться + удалить токен на сервере. */
	disable: () => Promise<void>;
}

export function useWebPushChannel(): WebPushChannel {
	const vapidPublicKey = useMemo(() => getVapidPublicKey(), []);
	const { permission, request } = usePushPermission();
	const registerMutation = api.pushTokens.registerToken.useMutation();
	const unregisterMutation = api.pushTokens.unregister.useMutation();

	// Refs для значений, используемых внутри стабильных callbacks.
	const permissionRef = useRef(permission);
	permissionRef.current = permission;
	const requestRef = useRef(request);
	requestRef.current = request;
	const registerRef = useRef(registerMutation.mutateAsync);
	registerRef.current = registerMutation.mutateAsync;
	const unregisterRef = useRef(unregisterMutation.mutateAsync);
	unregisterRef.current = unregisterMutation.mutateAsync;

	// Стабильные opts (deps = [vapidPublicKey], сам стабилен из useMemo([])) →
	// useWebPush возвращает стабильные subscribe/unsubscribe.
	const opts = useMemo<UseWebPushOptions>(
		() => ({
			vapidPublicKey,
			onSubscribe: async (input) => {
				await registerRef.current({
					platform: "web",
					deviceId: input.deviceId,
					// Для web `token` = endpoint (контракт требует min 8 символов).
					token: input.endpoint,
					endpoint: input.endpoint,
					p256dh: input.p256dh,
					auth: input.auth,
					deviceName:
						typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 120) : undefined,
					locale: input.locale,
					timezone: input.timezone,
				});
			},
			onUnsubscribe: async ({ endpoint }) => {
				await unregisterRef.current({ token: endpoint });
			},
		}),
		[vapidPublicKey],
	);

	const webPush = useWebPush(opts);
	const webPushRef = useRef(webPush);
	webPushRef.current = webPush;

	const enable = useCallback(async (): Promise<EnableResult> => {
		const wp = webPushRef.current;
		if (!wp.supported) return { ok: false, reason: "unsupported" };
		if (!vapidPublicKey) return { ok: false, reason: "noVapid" };
		let perm = permissionRef.current;
		if (perm === "default") perm = await requestRef.current();
		if (perm !== "granted") return { ok: false, reason: "denied" };
		try {
			await wp.subscribe();
			return { ok: true };
		} catch {
			return { ok: false, reason: "error" };
		}
	}, [vapidPublicKey]);

	const disable = useCallback(async () => {
		try {
			await webPushRef.current.unsubscribe();
		} catch {
			// best-effort — токен мог уже протухнуть на сервере.
		}
	}, []);

	return {
		supported: webPush.supported,
		vapidConfigured: Boolean(vapidPublicKey),
		permission,
		isSubscribed: webPush.subscription !== null,
		isBusy: webPush.loading || registerMutation.isPending || unregisterMutation.isPending,
		enable,
		disable,
	};
}
